// © Microsoft Corporation. All rights reserved.

import { ActionObserver, Observer } from ".";

import { ActionSender } from "./ActionSender";
import { GlobalActionSender } from "./GlobalActionSender";
import { IActionDispatchCancellationToken } from "./IActionDispatchCancellationToken";
import { IActionMap } from "./IActionMap";
import { IDataConnector } from "../connectors/IDataConnector";
import PageBase from "../pages";

/**
 * Class that maps Action to its callback function type.
 * Provides the method to get action creator which returns callback function to send to action.
 * Callback function dispatches the action payload.
 * @class
 */
export class ActionMap<TActionSignature extends Function> implements IActionMap<TActionSignature> {

    /**
     * An array of registered action observers
     */
    private observers: Observer[] = [];

    /**
     * Creates an instance of the `ActionMap` class.
     * @constructor
     * @param {string} type The action type, which is part of the IActionMap contract.
     */
    constructor(public type: string) {
    }

    /**
     * Method that returns an action sender function that can be used to send/dispatch the action to a specific connector
     * @method
     * @param {IDataConnector<any, any> | string} connector The connector instance to send the action to, or the connector namespace. If the param type
     * is string, then the connector instance will be looked up, and used if found.
     * @param {IActionDispatchCancellationToken} cancellationToken If passed in and cancellation has been requested, then nothing is dispatched.
     * @return {TActionSignature} return value is an object with a send method, which is used to send the action.
     */
    public getActionSender(connector: IDataConnector<any, any> | string, cancellationToken?: IActionDispatchCancellationToken): ActionSender<TActionSignature> | undefined {
        let connectorInstance: IDataConnector<any, any> = undefined;
        if (typeof (connector) === "string") {
            // Find the connector with the matching namespace
            connectorInstance = PageBase.getInstance().rootReducer.connector(connector);
        } else {
            connectorInstance = connector;
        }

        if (connectorInstance) {
            return new ActionSender<TActionSignature>(this.type, this.observers, connectorInstance, cancellationToken);
        }
    }

    /**
     * Method that returns an action sender function that can be used to send/dispatch the action globally to all connectors
     * @method
     * @param {string} experienceInstanceId An optional experienceInstanceId, that will get passed along in the action payload.
     * It can then be used by the receiving reducer to determine if the action should be handled.
     * @param {IActionDispatchCancellationToken} cancellationToken If passed in and cancellation has been requested, then nothing is dispatched.
     * @return {TActionSignature} return value is an object with a send method, which is used to send the action.
     */
    public getGlobalActionSender(experienceInstanceId?: string, cancellationToken?: IActionDispatchCancellationToken): GlobalActionSender<TActionSignature> {
        return new GlobalActionSender<TActionSignature>(this.type, this.observers, experienceInstanceId, cancellationToken);
    }

    /**
     * Method that registers a new observer for this action. The observer will be called after the action is dispatched
     * @method
     * @param {ActionObserver} observer The observer to be added to the list of observers that will be called when the action completes
     * @param {Object} connectors Option connectors associated to the observer that will be called by the observer
     */
    public registerObserver(observer: ActionObserver, connectors?: { dispatchConnector?: IDataConnector<any, any>, filterConnector?: IDataConnector<any, any> }): void {
        const { dispatchConnector, filterConnector } = (connectors || {});

        this.observers.push({ observer, dispatchConnector, filterConnector });
    }

    /**
     * Test method to help clear existing observers to reset the state in unit test scenarios.
     */
    public _clearObservers(): void {
        this.observers.length = 0;
    }
}